import OpenAI from 'openai';
import { supabase } from '../lib/supabase';
import { dealsService } from './crmService';

// Initialize OpenAI client
const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Analyzes a contact and generates a lead score using OpenAI GPT-5.
 * @param {Object} contact - Contact data with name, email, companyName, appointmentDate, salesRep
 * @param {Array} relatedDeals - Array of deals associated with this contact (via salesRep)
 * @returns {Promise<Object>} { score: number (0-100), reasoning: string }
 */
export async function scoreContact(contact, relatedDeals = []) {
  try {
    // Prepare contact context for AI analysis
    const contactContext = `
Contact Information:
- Name: ${contact?.name || 'N/A'}
- Email: ${contact?.email || 'N/A'}
- Company: ${contact?.companyName || 'N/A'}
- Appointment Date: ${contact?.appointmentDate || 'Not scheduled'}
- Sales Rep: ${contact?.salesRep || 'Unassigned'}
`;

    // Prepare related deals context
    let dealsContext = '';
    if (relatedDeals?.length > 0) {
      dealsContext = `\n\nRelated Deals (same sales rep):\n${relatedDeals?.map((deal, idx) => 
        `${idx + 1}. ${deal?.clientName} - $${deal?.dealValue?.toLocaleString()} - Stage: ${deal?.stage} - Close Date: ${deal?.expectedCloseDate}`
      )?.join('\n')}`;
    } else {
      dealsContext = '\n\nRelated Deals: None found';
    }

    const fullContext = contactContext + dealsContext;

    // Call OpenAI GPT-5 with structured output
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        {
          role: 'system',
          content: `You are an expert sales analyst specializing in lead scoring. Analyze the contact and their related deals to generate a lead quality score from 0-100.

Scoring Criteria:
- Company Quality (0-25): Assess company size, industry reputation, potential value
- Engagement Level (0-25): Appointment scheduled, email domain quality, sales rep assignment
- Deal History (0-25): Related deals, deal values, stages, success rate
- Timing & Urgency (0-25): Appointment proximity, deal close dates, pipeline velocity

Provide a score and clear reasoning explaining the key factors.`
        },
        {
          role: 'user',
          content: `Analyze this lead and provide a quality score:\n${fullContext}`
        }
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'lead_score_response',
          schema: {
            type: 'object',
            properties: {
              score: {
                type: 'number',
                description: 'Lead quality score from 0-100'
              },
              reasoning: {
                type: 'string',
                description: 'Detailed explanation of the score with key factors'
              }
            },
            required: ['score', 'reasoning'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'medium',
      verbosity: 'medium'
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    
    // Ensure score is within bounds
    const score = Math.max(0, Math.min(100, Math.round(result?.score)));
    
    return {
      score,
      reasoning: result?.reasoning || 'No reasoning provided'
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error scoring contact:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Scores a single contact and updates the database.
 * @param {string} contactId - Contact ID to score
 * @returns {Promise<Object>} Updated contact with score
 */
export async function scoreContactById(contactId) {
  try {
    // Fetch contact data
    const { data: contact, error: contactError } = await supabase?.from('contacts')?.select('*')?.eq('id', contactId)?.single();

    if (contactError) throw contactError;
    if (!contact) throw new Error('Contact not found');

    // Fetch related deals (matching by sales_rep)
    let relatedDeals = [];
    if (contact?.sales_rep) {
      const allDeals = await dealsService?.getAll();
      relatedDeals = allDeals?.filter(deal => 
        deal?.salesRep?.toLowerCase() === contact?.sales_rep?.toLowerCase()
      );
    }

    // Generate score
    const { score, reasoning } = await scoreContact(contact, relatedDeals);

    // Update contact with score
    const { data: updatedContact, error: updateError } = await supabase?.from('contacts')?.update({
        lead_score: score,
        lead_score_reasoning: reasoning,
        lead_score_updated_at: new Date()?.toISOString()
      })?.eq('id', contactId)?.select()?.single();

    if (updateError) throw updateError;

    // Convert to camelCase for consistency
    return {
      id: updatedContact?.id,
      name: updatedContact?.name,
      email: updatedContact?.email,
      companyName: updatedContact?.company_name,
      appointmentDate: updatedContact?.appointment_date,
      salesRep: updatedContact?.sales_rep,
      leadScore: updatedContact?.lead_score,
      leadScoreReasoning: updatedContact?.lead_score_reasoning,
      leadScoreUpdatedAt: updatedContact?.lead_score_updated_at,
      createdAt: updatedContact?.created_at,
      updatedAt: updatedContact?.updated_at
    };
  } catch (error) {
    throw error;
  }
}

/**
 * Scores multiple contacts in batch.
 * @param {Array<string>} contactIds - Array of contact IDs to score
 * @returns {Promise<Array>} Array of results with success/error status
 */
export async function scoreBatchContacts(contactIds) {
  const results = [];
  
  for (const contactId of contactIds) {
    try {
      const updatedContact = await scoreContactById(contactId);
      results?.push({
        contactId,
        success: true,
        contact: updatedContact
      });
    } catch (error) {
      results?.push({
        contactId,
        success: false,
        error: error?.message
      });
    }
  }
  
  return results;
}

export default {
  scoreContact,
  scoreContactById,
  scoreBatchContacts
};